import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AppRoutes } from './app/routes';
import { AuthProvider } from './lib/auth';
import { I18nProvider } from './lib/i18n';
import { ThemeProvider, readInitialTheme } from './lib/theme';
import { ToastProvider } from './components/Toast';
import { ApiError } from './lib/api';
import './styles/fonts.css';
import './styles/app.css';
import './styles/extra.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      /*
       * OQ-234 — quay lại tab thì làm mới.
       *
       * Trước đây đặt `false` (mặc định tôi chép vào từ M0, không phải quyết
       * định có cân nhắc — không có dòng nào trong docs giải thích nó). Hậu quả:
       * trạng thái tên miền đổi vì job nền, vì người khác thao tác, hoặc vì
       * chính người dùng vừa sửa trên dashboard Cloudflare — mà tab đang mở
       * không biết gì. Cách duy nhất để thấy sự thật là F5 bằng tay.
       *
       * Đo trước khi sửa: API **không** gửi `Cache-Control`/`ETag`, và máy chủ
       * **không** cache danh sách tên miền (chỉ cache 2FA challenge, refresh
       * token, chống replay TOTP). Nên đây là tầng duy nhất giữ dữ liệu cũ.
       */
      refetchOnWindowFocus: true,
      retry: (failureCount, error) => {
        // Không thử lại lỗi do người dùng/quyền — chỉ thử lại lỗi hạ tầng.
        if (error instanceof ApiError && error.status < 500) return false;
        return failureCount < 2;
      },
    },
  },
});

// FR-004: lần đầu theo prefers-color-scheme, sau đó nhớ lựa chọn của người dùng.
document.documentElement.setAttribute('data-theme', readInitialTheme());

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <I18nProvider>
      <ThemeProvider>
        <QueryClientProvider client={queryClient}>
          <ToastProvider>
            <BrowserRouter>
              <AuthProvider>
                <AppRoutes />
              </AuthProvider>
            </BrowserRouter>
          </ToastProvider>
        </QueryClientProvider>
      </ThemeProvider>
    </I18nProvider>
  </StrictMode>,
);
